import React, { useEffect, useMemo, useState } from "react";
import { Box, Typography, Button, Chip, Tooltip } from "@mui/material";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import PlayCircleOutlineIcon from "@mui/icons-material/PlayCircleOutline";
import { C } from "./reportingUtils";
import { BI_FEATURES } from "./BIWalkthrough";
import SpotlightTour from "./SpotlightTour";
import BITrialConsent from "./BITrialConsent";
import { readTrial, acceptTrial, deferTrial, trialDaysLeft, isTrialActive, hasDecidedTrial } from "./biTrial";
import { useSelector } from "react-redux";
import { notifyTeam } from "../utils/notifyTeam";

// Client-facing preview of BI & Reporting: a first-entry approval dialog for the
// 30-day free trial, plus a "walk me" guided explanation of every feature.
export default function BIReportingTrial({ tenantId, onTrialChange }) {
  const [trial, setTrial] = useState(() => readTrial(tenantId));
  const [tourOpen, setTourOpen] = useState(false);
  // Ask for approval automatically on the first visit.
  const [consentOpen, setConsentOpen] = useState(() => !hasDecidedTrial(tenantId));

  useEffect(() => {
    if (!hasDecidedTrial(tenantId)) setConsentOpen(true);
  }, [tenantId]);

  const daysLeft = useMemo(() => trialDaysLeft(trial), [trial]);
  const active = isTrialActive(trial);

  const email = useSelector((s) => s.userInfo.email);
  const apply = (record) => { setTrial(record); onTrialChange && onTrialChange(record); };
  const accept = () => {
    apply(acceptTrial(tenantId));
    // Alert the team that a prospect started the trial.
    notifyTeam({
      subject: `🚀 BI & Reporting trial started${email ? `: ${email}` : ""}`,
      message: `A user started the 30-day free trial.\n\nEmail: ${email || "n/a"}\nTenant: ${tenantId}`,
    });
  };
  const handleAccept = () => { accept(); setConsentOpen(false); setTourOpen(true); };
  const handleLater = () => { apply(deferTrial(tenantId)); setConsentOpen(false); };
  const startFromCta = () => { accept(); setTourOpen(true); };

  return (
    <Box sx={{ maxWidth: 1080, mx: "auto", px: { xs: 1, md: 2 }, py: 1 }}>
      {/* Hero */}
      <Box sx={{
        borderRadius: "18px", p: { xs: 3, md: 4 }, mb: 3, position: "relative", overflow: "hidden",
        background: "linear-gradient(135deg,#0E2A5A 0%,#1C4B9C 55%,#2F6BE0 100%)", color: "#fff",
      }}>
        <Box sx={{ position: "absolute", right: -60, top: -60, width: 240, height: 240, borderRadius: "50%", background: "rgba(255,255,255,.06)" }} />
        <Chip label="NEW" size="small" sx={{ height: 22, fontWeight: 800, letterSpacing: ".06em", fontSize: 11, color: "#0E2A5A", background: "#FFD34E", mb: 1.5 }} />
        <Typography sx={{ fontSize: { xs: 26, md: 32 }, fontWeight: 800, letterSpacing: "-.01em", mb: 1 }}>
          BI &amp; Reporting
        </Typography>
        <Typography sx={{ fontSize: 15.5, lineHeight: 1.6, maxWidth: 620, opacity: 0.92 }}>
          Turn your portfolio into decisions — live dashboards, risk &amp; exposure analytics, AI insights,
          a report builder, threshold alerts and scheduled exports. All in one place.
        </Typography>

        {!active ? (
          <Box sx={{ mt: 3, display: "flex", flexWrap: "wrap", alignItems: "center", gap: 1.5 }}>
            <Tooltip title="Unlock all BI & Reporting features free for 30 days — no credit card needed" arrow>
              <Button onClick={startFromCta} variant="contained" startIcon={<AutoAwesomeIcon />}
                sx={{ textTransform: "none", fontWeight: 800, fontSize: 15, borderRadius: "12px", px: 3, py: 1.1,
                  background: "#fff", color: "#153C82", "&:hover": { background: "#F0F5FF" } }}>
                Start your 30-day free trial
              </Button>
            </Tooltip>
            <Tooltip title="Take a guided tour of every feature before you dive in" arrow>
              <Button onClick={() => setTourOpen(true)} startIcon={<PlayCircleOutlineIcon />}
                sx={{ textTransform: "none", fontWeight: 700, color: "#fff", borderRadius: "12px",
                  border: "1px solid rgba(255,255,255,.5)", px: 2.5, py: 1.1, "&:hover": { background: "rgba(255,255,255,.12)" } }}>
                Walk me through it
              </Button>
            </Tooltip>
            <Typography sx={{ fontSize: 12.5, opacity: 0.8, width: "100%", mt: 0.5 }}>
              It's on us — no credit card needed. Our team will reach out during the month to help you get the most out of it.
            </Typography>
          </Box>
        ) : (
          <Box sx={{ mt: 3, display: "flex", flexWrap: "wrap", alignItems: "center", gap: 1.5 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, background: "rgba(255,255,255,.14)", borderRadius: "12px", px: 2, py: 1.1 }}>
              <CheckCircleIcon sx={{ color: "#8CF0B8" }} />
              <Box>
                <Typography sx={{ fontSize: 14, fontWeight: 800 }}>Free trial active · {daysLeft} days left</Typography>
                <Typography sx={{ fontSize: 12, opacity: 0.85 }}>We'll be in touch during the month to get you fully set up.</Typography>
              </Box>
            </Box>
            <Tooltip title="Take a guided tour of every feature" arrow>
              <Button onClick={() => setTourOpen(true)} startIcon={<PlayCircleOutlineIcon />}
                sx={{ textTransform: "none", fontWeight: 700, color: "#fff", borderRadius: "12px",
                  border: "1px solid rgba(255,255,255,.5)", px: 2.5, py: 1.1, "&:hover": { background: "rgba(255,255,255,.12)" } }}>
                Walk me through it
              </Button>
            </Tooltip>
          </Box>
        )}
      </Box>

      {/* Feature grid with explainability */}
      <Typography sx={{ fontSize: 13, fontWeight: 700, letterSpacing: ".06em", textTransform: "uppercase", color: C.muted, mb: 1.5 }}>
        What's inside
      </Typography>
      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr", lg: "1fr 1fr 1fr" }, gap: 1.75 }}>
        {BI_FEATURES.map((f, idx) => {
          const Icon = f.Icon;
          return (
            <Box key={f.key} data-tour={`bi-card-${idx}`} sx={{ border: `1px solid ${C.line}`, borderRadius: "14px", p: 2.25, background: "#fff",
              transition: "all .16s ease", "&:hover": { boxShadow: "0 8px 22px rgba(16,37,74,.08)", transform: "translateY(-2px)" } }}>
              <Box sx={{ width: 40, height: 40, borderRadius: "11px", display: "grid", placeItems: "center", background: `${f.color}14`, mb: 1.25 }}>
                <Icon sx={{ fontSize: 21, color: f.color }} />
              </Box>
              <Typography sx={{ fontSize: 15, fontWeight: 700, color: C.ink, mb: 0.5 }}>{f.label}</Typography>
              <Typography sx={{ fontSize: 13, lineHeight: 1.55, color: C.muted }}>{f.blurb}</Typography>
            </Box>
          );
        })}
      </Box>

      <Typography sx={{ fontSize: 12, color: C.muted, textAlign: "center", mt: 3 }}>
        During your trial our team will reach out to tailor BI &amp; Reporting to your portfolio and answer any questions.
      </Typography>

      <BITrialConsent
        open={consentOpen}
        onAccept={handleAccept}
        onLater={handleLater}
        onWalkthrough={() => { setConsentOpen(false); setTourOpen(true); }}
      />
      <SpotlightTour
        open={tourOpen}
        steps={BI_FEATURES.map((f, idx) => ({ selector: `[data-tour="bi-card-${idx}"]`, title: f.label, text: f.blurb }))}
        onClose={() => setTourOpen(false)}
      />
    </Box>
  );
}
